import { useEffect, useState } from 'react'
import type {
  DrawLotteryWithCurrent, DrawBetType, DrawBetTypeConfig, OtpConfirmation,
} from '@nexbit/types'
import { apiDrawGetLotteries, apiDrawUpdatePayout, ApiRequestError } from '@/lib/api'
import { OtpConfirmDialog } from '@/components/OtpConfirmDialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { AlertCircle, AlertTriangle, Check, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useT } from '@/contexts/language'
import { useCan } from '@/hooks/usePermission'

const BET_TYPES: DrawBetType[] = [
  '3top', '3tote', '3front', '3bottom', '2top', '2bottom', 'run_top', 'run_bottom',
  'odd_even_top', 'high_low_top', 'odd_even_bottom', 'high_low_bottom',
]

/**
 * Payout ceilings shown as hints. The server enforces the same numbers — this copy
 * exists so the form can flag a bad value before a round trip, not as the guard.
 */
const MAX_PAYOUT: Record<DrawBetType, number> = {
  '3top': 1200, '3tote': 300, '2top': 150, '2bottom': 150, '3front': 500, '3bottom': 500,
  run_top: 20, run_bottom: 20, odd_even_top: 5, high_low_top: 5,
  odd_even_bottom: 5, high_low_bottom: 5,
}

const DEFAULT_BET_CFG: DrawBetTypeConfig = {
  enabled: false, payout: 1, minBet: 1, maxBet: 500, maxPerNumber: 1000, maxPerType: 50000,
}

type EditableCfg = Record<DrawBetType, DrawBetTypeConfig>

/**
 * What each bet type pays and the smallest stake it takes.
 *
 * Separate from the intake caps on the number-limits page on purpose: a payout rate
 * is the one number here that can hand money out, so it is priced deliberately and
 * saving re-asks for the admin's password. Caps only ever restrict, and are adjusted
 * far more often, so they sit where the stake is being watched.
 */
export default function PayoutRatesPage() {
  const t = useT()
  const can = useCan()

  const [lotteries, setLotteries] = useState<DrawLotteryWithCurrent[]>([])
  const [lotteryId, setLotteryId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [cfg, setCfg] = useState<EditableCfg | null>(null)
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [saved, setSaved] = useState(false)
  // The password already proves who is at the keyboard; the code proves someone else
  // saw the rates change. Snapshotted so what is approved is what is submitted.
  const [pending, setPending] = useState<{
    password: string
    betConfig: Partial<Record<DrawBetType, DrawBetTypeConfig>>
  } | null>(null)

  const lottery = lotteries.find((l) => l.id === lotteryId) ?? null
  const bettingOpen = lottery?.currentDraw?.status === 'open'

  useEffect(() => {
    let alive = true
    void (async () => {
      try {
        const data = await apiDrawGetLotteries()
        if (!alive) return
        setLotteries(data)
        setLotteryId((prev) => prev ?? data[0]?.id ?? null)
      } catch (e) {
        if (alive) setError(e instanceof ApiRequestError ? e.message : t.common.error)
      } finally {
        if (alive) setLoading(false)
      }
    })()
    return () => { alive = false }
  }, [t])

  // Reload the editor whenever the lottery in view changes.
  useEffect(() => {
    if (!lottery) return
    setCfg(
      Object.fromEntries(
        BET_TYPES.map((k) => [k, { ...DEFAULT_BET_CFG, ...lottery.betConfig[k] }]),
      ) as EditableCfg,
    )
    setPassword('')
    setSaved(false)
  }, [lottery])

  const set = (k: DrawBetType, patch: Partial<DrawBetTypeConfig>) =>
    setCfg((prev) => (prev ? { ...prev, [k]: { ...prev[k], ...patch } } : prev))

  const invalid = cfg
    ? BET_TYPES.filter((k) => {
        const c = cfg[k]
        // Caps live on the number-limits page; this form only prices bets.
        return !(c.payout > 0) || c.payout > MAX_PAYOUT[k] || !(c.minBet > 0)
      })
    : []

  // Only send what actually changed, and show the same list as the confirmation.
  const changes = cfg && lottery
    ? BET_TYPES.flatMap((k) => {
        const before = lottery.betConfig[k]
        const after = cfg[k]
        // A bet type this lottery never had, still off, is "not offered" either way — not a
        // change to record. Without this every save on a lottery that lacks เลขหน้า/ท้าย 3
        // ตัว (all but the government lottery) would write two disabled entries into it.
        if (!before && !after.enabled) return []
        const fields: { label: string; from: string; to: string }[] = []
        const cmp = (label: string, a: number | boolean | undefined, b: number | boolean) => {
          if (a !== b) fields.push({ label, from: a === undefined ? '—' : String(a), to: String(b) })
        }
        cmp(t.draws.payoutEnabled, before?.enabled, after.enabled)
        cmp(t.draws.payoutRate, before?.payout, after.payout)
        cmp(t.draws.payoutMin, before?.minBet, after.minBet)
        return fields.length ? [{ betType: k, fields }] : []
      })
    : []

  const canSubmit = changes.length > 0 && invalid.length === 0 && password.length > 0

  function askToSubmit() {
    if (!lottery || !cfg) return
    setError(null)
    setPending({
      password,
      betConfig: Object.fromEntries(changes.map((c) => [c.betType, cfg[c.betType]])),
    })
  }

  async function handleSubmit(
    body: { password: string; betConfig: Partial<Record<DrawBetType, DrawBetTypeConfig>> },
    otp: OtpConfirmation,
  ) {
    if (!lottery) return
    setSubmitting(true)
    try {
      await apiDrawUpdatePayout(lottery.id, body, otp)
      setLotteries(await apiDrawGetLotteries())
      setSaved(true)
      setPassword('')
    } finally {
      setSubmitting(false)
    }
  }

  const num = (
    k: DrawBetType,
    field: 'payout' | 'minBet',
    bad: boolean,
  ) => (
    <Input
      inputMode="decimal"
      value={cfg ? String(cfg[k][field]) : ''}
      onChange={(e) => {
        const v = e.target.value.replace(/[^\d.]/g, '')
        set(k, { [field]: v === '' ? 0 : Number(v) })
      }}
      aria-invalid={bad}
      className={cn('h-8 w-full font-mono text-xs tabular-nums', bad && 'border-destructive')}
    />
  )

  return (
    <div className="p-4 md:p-6 max-w-4xl">
      <div className="mb-5">
        <span className="font-mono text-xs font-bold uppercase tracking-wider text-foreground">
          {t.nav.payoutRates}
        </span>
        <p className="mt-1 font-mono text-[11px] text-muted-foreground">{t.draws.payoutDesc}</p>
      </div>

      {error && (
        <div className="mb-4 flex items-center gap-2 rounded-[var(--radius-sm)] border border-destructive/40 bg-card p-2">
          <AlertCircle className="h-3.5 w-3.5 shrink-0 text-destructive" />
          <p className="font-mono text-xs text-destructive">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="space-y-2">
          <Skeleton className="h-9 w-64" />
          <Skeleton className="h-48 w-full" />
        </div>
      ) : (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              {t.draws.lotteries}
            </Label>
            <select
              value={lotteryId ?? ''}
              onChange={(e) => setLotteryId(e.target.value)}
              className="h-9 rounded-[var(--radius-sm)] border border-input bg-background px-2 font-mono text-sm text-foreground"
            >
              {lotteries.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}{l.currentDraw?.status === 'open' ? ' ●' : ''}
                </option>
              ))}
            </select>
            {saved && changes.length === 0 && (
              <span className="flex items-center gap-1 font-mono text-[11px] text-success">
                <Check className="h-3 w-3" /> {t.draws.payoutSaved}
              </span>
            )}
          </div>

          {bettingOpen && (
            <div className="mb-4 flex items-start gap-2 rounded-[var(--radius-sm)] border border-warning/40 bg-warning/10 p-2.5">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning" />
              <p className="font-mono text-[11px] leading-relaxed text-foreground">
                {t.draws.payoutOpenWarning}
              </p>
            </div>
          )}

          <div className="overflow-hidden rounded-[var(--radius)] border border-border bg-card shadow-[var(--shadow-sm)]">
            <table className="w-full">
              <thead className="bg-secondary">
                <tr className="text-left">
                  <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-secondary-foreground">
                    {t.draws.payoutBetType}
                  </th>
                  <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-secondary-foreground">
                    {t.draws.payoutEnabled}
                  </th>
                  <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-secondary-foreground">
                    {t.draws.payoutRate}
                  </th>
                  <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-secondary-foreground">
                    {t.draws.payoutMin}
                  </th>
                </tr>
              </thead>
              <tbody>
                {cfg && BET_TYPES.map((k) => {
                  const c = cfg[k]
                  const rateBad = (!(c.payout > 0) || c.payout > MAX_PAYOUT[k])
                  return (
                    <tr key={k} className="border-t border-border">
                      <td className="px-3 py-2">
                        <div className="font-mono text-xs text-foreground">
                          {t.draws.betTypeLabel[k]}
                        </div>
                        <div className="font-mono text-[10px] text-muted-foreground">
                          {t.draws.payoutCeiling(MAX_PAYOUT[k])}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          role="switch"
                          aria-checked={c.enabled}
                          onClick={() => set(k, { enabled: !c.enabled })}
                          className={cn(
                            'h-7 rounded-[var(--radius-sm)] border px-2 font-mono text-[11px] transition-colors',
                            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                            c.enabled
                              ? 'border-success/40 bg-success/10 text-success'
                              : 'border-border bg-card text-muted-foreground',
                          )}
                        >
                          {c.enabled ? t.common.active : t.common.inactive}
                        </button>
                      </td>
                      <td className="w-32 px-3 py-2">
                        {num(k, 'payout', rateBad)}
                      </td>
                      <td className="w-32 px-3 py-2">
                        {num(k, 'minBet', c.minBet <= 0)}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {invalid.length > 0 && (
            <p className="mt-3 font-mono text-[11px] text-destructive">
              {t.draws.payoutInvalid(invalid.map((k) => t.draws.betTypeLabel[k]).join(', '))}
            </p>
          )}

          {changes.length > 0 && invalid.length === 0 && (
            <div className="mt-3 space-y-1 rounded-[var(--radius-sm)] border border-border bg-card p-3">
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                {t.draws.payoutChanges}
              </p>
              {changes.map((c) => (
                <div key={c.betType} className="font-mono text-[11px]">
                  <span className="text-foreground">{t.draws.betTypeLabel[c.betType]}</span>
                  {c.fields.map((f) => (
                    <span key={f.label} className="ml-2 text-muted-foreground">
                      {f.label} <span className="line-through">{f.from}</span>
                      {' → '}
                      <span className="font-semibold text-foreground">{f.to}</span>
                    </span>
                  ))}
                </div>
              ))}
            </div>
          )}

          <div className="mt-4 flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1">
              <Label
                htmlFor="payout-password"
                className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground"
              >
                {t.draws.payoutPassword}
              </Label>
              <Input
                id="payout-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-9 w-64 font-mono text-sm"
              />
              <p className="font-mono text-[10px] text-muted-foreground">
                {t.draws.payoutPasswordHint}
              </p>
            </div>
            {can('payout-rates:edit') && (
            <Button
              size="sm"
              onClick={askToSubmit}
              className="mb-5 h-9 gap-1.5 font-mono text-xs"
              disabled={!canSubmit || submitting}
            >
              {submitting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
              {submitting ? t.draws.submitting : t.draws.payoutSubmit}
            </Button>
            )}
          </div>
        </>
      )}

      {pending && lottery && (
        <OtpConfirmDialog
          purpose="draw.payout.update"
          payload={{ params: { lotteryId: lottery.id }, body: { ...pending } }}
          title="ยืนยันการแก้อัตราจ่าย"
          description={`${lottery.name} — แก้ ${changes.length} ประเภทเดิมพัน ` +
            `(${changes.map((c) => c.betType).join(', ')})`}
          confirmLabel="บันทึกอัตราจ่าย"
          onConfirm={(otp) => handleSubmit(pending, otp)}
          onDone={() => setPending(null)}
          onClose={() => setPending(null)}
        />
      )}
    </div>
  )
}
